// Ghost Driver overlays — web-port 1-op-1 van AutoDemoOverlay.tsx
// (intro-banner, control-bar, e-mail preview, intelligence reveal,
//  wooncoach-bubble, auto-badge, endscreen)
import React from 'react';
import Icon from '../Icon';

const NAVY = '#0e2340';
const NAVY_DEEP = '#08182f';
const GOLD = '#c48a3a';
const GOLD_SOFT = 'rgba(196,138,58,0.35)';

const ACCENTS = { gold: '#c48a3a', navy: '#38a3d1', green: '#22C55E', red: '#B3261E', pink: '#e0559c' };

// ── Intro banner: "Demo-omgeving · fictieve gegevens · 2.0" ─────
export function IntroBanner({ show, isV2, onStart, autoplay }) {
  if (!show) return null;
  return (
    <div data-testid="ghost-intro" className="anim-fade-down" style={{
      position: 'absolute', top: 10, left: 20, right: 20, zIndex: 10000,
      display: 'flex', justifyContent: 'center', pointerEvents: autoplay ? 'none' : 'auto',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'rgba(14,35,64,0.94)', padding: '10px 16px',
        borderRadius: 999, border: `1px solid ${GOLD_SOFT}`,
        boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
      }}>
        <Icon name="play" size={13} color={GOLD} />
        <span style={{ color: '#fff', fontSize: 12.5, fontWeight: 700, letterSpacing: 0.2 }}>
          Demo-omgeving · fictieve gegevens{isV2 ? ' · 2.0' : ''}
        </span>
        {!autoplay ? (
          <button className="immo-btn" data-testid="ghost-start" onClick={onStart} style={{
            background: GOLD, borderRadius: 999, padding: '5px 12px',
            color: NAVY, fontSize: 11.5, fontWeight: 900, marginLeft: 4,
          }}>
            Start ▶
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ── Toast (UX-notificatie uit het script) ───────────────────────
export function GhostToast({ toast }) {
  if (!toast) return null;
  const accent = ACCENTS[toast.accent] || GOLD;
  return (
    <div data-testid="ghost-toast" className="anim-fade-down" style={{
      position: 'absolute', top: 46, left: 12, right: 12, zIndex: 9999, pointerEvents: 'none',
    }}>
      <div style={{
        background: 'rgba(14,35,64,0.96)', borderRadius: 14, padding: '11px 13px',
        border: `1px solid ${GOLD_SOFT}`, borderLeft: `3px solid ${accent}`,
        display: 'flex', gap: 10, alignItems: 'flex-start',
        boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
      }}>
        <Icon name={toast.icon || 'bell'} size={15} color={accent} style={{ marginTop: 1, flexShrink: 0 }} />
        <div>
          <div style={{ color: '#fff', fontSize: 12.5, fontWeight: 800 }}>{toast.title}</div>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11.5, marginTop: 2, lineHeight: '15px' }}>{toast.body}</div>
        </div>
      </div>
    </div>
  );
}

// ── Auto-badge: "🤖 automatisch door IMMO MATCH" ────────────────
export function AutoBadge({ show }) {
  if (!show) return null;
  return (
    <div data-testid="ghost-auto-badge" className="anim-fade-in" style={{
      position: 'absolute', top: 40, left: 0, right: 0, zIndex: 9995,
      display: 'flex', justifyContent: 'center', pointerEvents: 'none',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(14,35,64,0.86)', padding: '6px 12px',
        borderRadius: 999, border: `1px solid ${GOLD_SOFT}`,
      }}>
        <span style={{ fontSize: 12 }}>🤖</span>
        <span style={{ color: GOLD, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4 }}>automatisch door IMMO MATCH</span>
      </div>
    </div>
  );
}

// ── E-mail preview modal ────────────────────────────────────────
export function EmailPreview({ email, onClose }) {
  if (!email) return null;
  return (
    <div data-testid="ghost-email-modal" className="anim-fade-down" style={{
      position: 'absolute', top: 80, left: 16, right: 16, zIndex: 9999,
      display: 'flex', justifyContent: 'center',
    }}>
      <div style={{
        background: NAVY, borderRadius: 18, padding: 16, width: '100%',
        border: `1.2px solid rgba(196,138,58,0.5)`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ background: GOLD, padding: '4px 10px', borderRadius: 999, color: NAVY, fontSize: 10, fontWeight: 900, letterSpacing: 1.5 }}>DEMO E-MAIL</span>
          <button className="immo-btn" data-testid="ghost-email-close" onClick={onClose} style={{ padding: 6 }}>
            <Icon name="x" size={18} color="rgba(255,255,255,0.85)" />
          </button>
        </div>
        {[['Aan', email.to], ['Onderwerp', email.subject]].map(([l, v], i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
            <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 800, letterSpacing: 0.6, width: 92, marginTop: 2, textTransform: 'uppercase', flexShrink: 0 }}>{l}</span>
            <span style={{ color: '#fff', fontSize: 13, flex: 1, fontWeight: 600 }}>{v}</span>
          </div>
        ))}
        <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: 12, minHeight: 90 }}>
          <pre style={{ color: '#fff', fontSize: 12.5, lineHeight: '18px', fontFamily: 'Menlo, monospace', whiteSpace: 'pre-wrap', margin: 0 }}>{email.body}</pre>
        </div>
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="info" size={12} color="rgba(255,255,255,0.55)" style={{ flexShrink: 0 }} />
          <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10.5, flex: 1, lineHeight: '14px' }}>
            DEMO — niet echt verzonden. In productie gaat dit rechtstreeks naar het makelaarskantoor.
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Intelligence reveal ─────────────────────────────────────────
export function IntelligenceReveal({ reveal, mode }) {
  if (!reveal) return null;
  return (
    <div data-testid="ghost-reveal" style={{
      position: 'absolute', inset: 0, zIndex: 9997,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(8,24,47,0.72)',
    }}>
      <div className="anim-pop" style={{
        background: NAVY_DEEP, borderRadius: 22, padding: '26px 24px',
        width: '86%', maxWidth: 380, textAlign: 'center',
        border: `1.2px solid ${GOLD_SOFT}`, boxShadow: '0 14px 40px rgba(0,0,0,0.5)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: GOLD, display: 'inline-block' }} />
          <span style={{ color: GOLD, fontSize: 10.5, fontWeight: 900, letterSpacing: 2.4, textTransform: 'uppercase' }}>IMMO MATCH analyseert</span>
        </div>
        {/* Venn */}
        <div style={{ width: 180, height: 90, margin: '0 auto 22px', position: 'relative' }}>
          <div className="anim-fade-in" style={{ animationDelay: '.2s', position: 'absolute', left: 15, width: 90, height: 90, borderRadius: 45, background: 'rgba(74,127,201,0.18)', border: '1.5px solid rgba(74,127,201,0.6)' }} />
          <div className="anim-fade-in" style={{ animationDelay: '.5s', position: 'absolute', right: 15, width: 90, height: 90, borderRadius: 45, background: 'rgba(196,138,58,0.18)', border: '1.5px solid rgba(196,138,58,0.7)' }} />
          <div className="anim-pop" style={{ animationDelay: '1.1s', position: 'absolute', left: 69, top: 24, width: 42, height: 42, borderRadius: 21, background: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 14px rgba(196,138,58,0.6)' }}>
            <Icon name="zap" size={22} color={NAVY} />
          </div>
        </div>
        <div style={{ color: '#fff', fontSize: 17, fontWeight: 900, letterSpacing: -0.2, marginBottom: 8 }}>{reveal.title}</div>
        <div className="anim-fade-in" style={{ animationDelay: '1.5s', color: 'rgba(255,255,255,0.82)', fontSize: 13.5, lineHeight: '19px', fontWeight: 500 }}>{reveal.subtitle}</div>
        <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.14)' }}>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
            {mode === 'couple' ? 'Wooncoach past jullie feed automatisch aan' : 'Wooncoach past jouw feed automatisch aan'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Wooncoach bubble ────────────────────────────────────────────
export function WooncoachBubble({ bubble }) {
  if (!bubble) return null;
  return (
    <div data-testid="ghost-bubble" style={{
      position: 'absolute', left: 16, right: 16, bottom: 84, zIndex: 9996,
      display: 'flex', justifyContent: 'center', pointerEvents: 'none',
    }}>
      <div className="anim-rise" style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        background: 'rgba(255,255,255,0.98)', borderRadius: 16,
        padding: '12px 14px', width: '100%',
        borderLeft: `4px solid ${GOLD}`, boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
      }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2, overflow: 'hidden', flexShrink: 0 }}>
          <img src="/logo_official.png" alt="IMMO MATCH" style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: NAVY, fontSize: 13, fontWeight: 900, letterSpacing: -0.2, marginBottom: 3 }}>{bubble.title}</div>
          <div style={{ color: 'rgba(14,35,64,0.85)', fontSize: 12.5, lineHeight: '17.5px', fontWeight: 500 }}>{bubble.body}</div>
        </div>
      </div>
    </div>
  );
}

// ── Endscreen ───────────────────────────────────────────────────
export function EndScreen({ end, onAction }) {
  if (!end) return null;
  return (
    <div data-testid="ghost-endscreen" style={{
      position: 'absolute', inset: 0, zIndex: 10001,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(8,24,47,0.92)', padding: 20,
    }}>
      <div className="anim-pop" style={{
        background: NAVY, borderRadius: 24, padding: '32px 26px',
        width: '100%', maxWidth: 400, textAlign: 'center',
        border: `1.2px solid ${GOLD_SOFT}`, boxShadow: '0 16px 44px rgba(0,0,0,0.5)',
      }}>
        <div style={{ width: 110, height: 110, margin: '0 auto 14px' }}>
          <img src="/logo_official.png" alt="IMMO MATCH" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <div style={{ color: '#fff', fontSize: 22, fontWeight: 900, letterSpacing: -0.3, marginBottom: 8 }}>Dit is IMMO MATCH</div>
        <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13.5, lineHeight: '20px', marginBottom: 24 }}>
          {end.subtitle || 'Van woonvoorkeur naar match, bezoek en woonreis.'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="immo-btn" data-testid="ghost-cta-open-app" onClick={() => onAction('open_app')} style={{
            background: GOLD, padding: '14px 20px', borderRadius: 14, minHeight: 48,
            color: NAVY, fontSize: 15, fontWeight: 900, letterSpacing: 0.2, textAlign: 'center',
          }}>
            Open IMMO MATCH
          </button>
          {end.crossMode ? (
            <button className="immo-btn" data-testid="ghost-cta-switch-mode" onClick={() => onAction('switch_mode')} style={{
              background: 'rgba(196,138,58,0.12)', border: '1px solid rgba(196,138,58,0.5)',
              padding: '13px 20px', borderRadius: 14, minHeight: 46,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              color: GOLD, fontSize: 14, fontWeight: 900,
            }}>
              <Icon name={end.crossMode === 'couple' ? 'heart' : 'user'} size={14} color={GOLD} />
              {end.crossMode === 'couple' ? 'Bekijk de Koppeldemo →' : 'Bekijk de Solodemo →'}
            </button>
          ) : null}
          <button className="immo-btn" data-testid="ghost-cta-replay" onClick={() => onAction('replay')} style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
            padding: '12px 20px', borderRadius: 14, minHeight: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            color: GOLD, fontSize: 13.5, fontWeight: 800,
          }}>
            <Icon name="rotate-ccw" size={14} color={GOLD} />
            Demo opnieuw bekijken
          </button>
          <button className="immo-btn" data-testid="ghost-cta-back-site" onClick={() => onAction('back_to_site')} style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
            padding: '12px 20px', borderRadius: 14, minHeight: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            color: 'rgba(255,255,255,0.75)', fontSize: 13.5, fontWeight: 700,
          }}>
            <Icon name="chevron-left" size={14} color="rgba(255,255,255,0.75)" />
            Terug naar website
          </button>
        </div>
        <div style={{ marginTop: 22, color: 'rgba(255,255,255,0.42)', fontSize: 11, fontStyle: 'italic' }}>Dit was een demo van IMMO MATCH.</div>
      </div>
    </div>
  );
}

// ── Control bar (onderaan het frame) ────────────────────────────
export function ControlBar({ isV2, status, label, current, finished, onPrev, onPlayPause, onNext, onReset, onStop, mode }) {
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '0 16px 12px', zIndex: 9998, display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
      <div style={{
        pointerEvents: 'auto', display: 'flex', alignItems: 'center',
        background: 'rgba(14,35,64,0.88)', borderRadius: 18, padding: '7px 8px', gap: 4,
        maxWidth: 380, border: `1px solid ${GOLD_SOFT}`, boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
      }}>
        {isV2 ? (
          <button className="immo-btn" data-testid="ghost-prev" onClick={onPrev} aria-label="Vorige" style={{ width: 28, height: 28, borderRadius: 14, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="skip-back" size={15} color="#fff" />
          </button>
        ) : null}
        <button className="immo-btn" data-testid="ghost-play-pause" onClick={onPlayPause} aria-label={status === 'running' ? 'Pauze' : 'Start'} style={{ width: 30, height: 30, borderRadius: 15, background: 'rgba(255,255,255,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={status === 'running' ? 'pause' : 'play'} size={18} color="#fff" />
        </button>
        <div data-testid="ghost-step-info" style={{ minWidth: 90, maxWidth: 180, padding: '0 6px' }}>
          <div data-testid="ghost-step-counter" style={{ color: GOLD, fontSize: 9, fontWeight: 900, letterSpacing: 1.4 }}>{label}</div>
          <div style={{ color: '#fff', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{current?.title || ''}</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{current?.subtitle || ''}</div>
        </div>
        {!finished ? (
          <button className="immo-btn" data-testid="ghost-next" onClick={onNext} aria-label="Volgende" style={{ width: 30, height: 30, borderRadius: 15, background: 'rgba(255,255,255,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="skip-forward" size={17} color="#fff" />
          </button>
        ) : null}
        <button className="immo-btn" data-testid="ghost-reset" onClick={onReset} aria-label="Reset" style={{ width: 30, height: 30, borderRadius: 15, background: 'rgba(255,255,255,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="rotate-ccw" size={17} color="#fff" />
        </button>
        <button className="immo-btn" data-testid="ghost-stop" onClick={onStop} aria-label="Sluiten" style={{ width: 28, height: 28, borderRadius: 14, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="x" size={16} color="#f8f6f2" />
        </button>
      </div>
      <div style={{
        marginTop: 4, display: 'flex', alignItems: 'center', gap: 5,
        background: 'rgba(14,35,64,0.78)', padding: '3px 8px', borderRadius: 999,
      }}>
        <span style={{ width: 5, height: 5, borderRadius: 2.5, background: '#4ade80', display: 'inline-block' }} />
        <span style={{ color: '#fff', fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3 }}>
          {mode === 'couple' ? 'Auto-demo Koppel' : 'Auto-demo Solo'}{isV2 ? ' · 2.0' : ''}
        </span>
      </div>
    </div>
  );
}
